<!-- eslint-disable global-require -->
<template>
  <section class="join-page">
    <div class="join-bg" aria-hidden="true"></div>

    <div class="mx-auto w-full max-w-8xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div class="mb-8 sm:mb-10 text-center">
        <p
          class="inline-flex items-center gap-2 rounded-full bg-white/70 dark:bg-ternary-dark/70 px-4 py-2 text-sm text-ternary-dark dark:text-ternary-light shadow-sm ring-1 ring-black/5 dark:ring-white/10 backdrop-blur"
        >
          <span class="join-badge-dot"></span>
          Gulftic Team Onboarding
        </p>
        <h1
          class="mt-4 text-3xl sm:text-4xl font-general-semibold text-primary-dark dark:text-primary-light"
        >
          Join Gulftic
        </h1>
        <p
          class="mt-2 text-base sm:text-lg text-ternary-dark dark:text-ternary-light/90"
        >
          A quick 3-step setup. You can go back anytime before submitting.
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
        <!-- Left: Steps -->
        <aside class="lg:col-span-4">
          <div
            class="rounded-2xl bg-white/80 dark:bg-ternary-dark/70 backdrop-blur shadow-sm ring-1 ring-black/5 dark:ring-white/10 p-5 sm:p-6"
          >
            <div class="flex items-center justify-between">
              <p
                class="text-sm font-general-medium text-ternary-dark dark:text-ternary-light/90"
              >
                Progress
              </p>
              <p
                class="text-sm font-general-semibold text-primary-dark dark:text-primary-light"
              >
                {{ currentStep }}/{{ steps.length }}
              </p>
            </div>
            <div
              class="mt-3 h-2 w-full rounded-full bg-gray-200/70 dark:bg-black/20 overflow-hidden"
            >
              <div
                class="h-full rounded-full join-progress"
                :style="{ width: `${progressPercent}%` }"
              ></div>
            </div>

            <ol class="mt-6 space-y-3">
              <li v-for="(s, idx) in steps" :key="s.key">
                <button
                  type="button"
                  class="w-full text-left rounded-xl p-4 transition ring-1"
                  :class="stepBtnClass(idx + 1)"
                  @click="goToStep(idx + 1)"
                >
                  <div class="flex items-start gap-3">
                    <span class="join-step-icon">
                      <i :data-feather="s.icon"></i>
                    </span>
                    <div class="flex-1">
                      <p
                        class="font-general-semibold text-primary-dark dark:text-primary-light"
                      >
                        {{ s.title }}
                      </p>
                      <p
                        class="mt-1 text-sm text-ternary-dark dark:text-ternary-light/80"
                      >
                        {{ s.subtitle }}
                      </p>
                    </div>
                    <span
                      class="mt-1 text-xs font-general-semibold join-step-pill"
                      :class="stepPillClass(idx + 1)"
                    >
                      {{ stepStateLabel(idx + 1) }}
                    </span>
                  </div>
                </button>
              </li>
            </ol>

            <div
              class="mt-6 rounded-xl bg-indigo-50/70 dark:bg-indigo-500/10 p-4 ring-1 ring-indigo-600/10 dark:ring-indigo-400/10"
            >
              <p class="text-sm text-ternary-dark dark:text-ternary-light/90">
                Tip: Use a clear headshot (good lighting). It will appear on
                your vCard profile.
              </p>
            </div>
          </div>
        </aside>

        <!-- Right: Form card -->
        <div class="lg:col-span-8">
          <div
            class="rounded-2xl bg-white/90 dark:bg-ternary-dark/80 backdrop-blur shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden"
          >
            <div
              class="px-5 sm:px-7 py-5 sm:py-6 border-b border-black/5 dark:border-white/10"
            >
              <p class="text-sm text-ternary-dark dark:text-ternary-light/80">
                Step {{ currentStep }} of {{ steps.length }}
              </p>
              <h2
                class="mt-1 text-xl sm:text-2xl font-general-semibold text-primary-dark dark:text-primary-light"
              >
                {{ steps[currentStep - 1].title }}
              </h2>
            </div>

            <form
              class="px-5 sm:px-7 py-6 sm:py-7"
              novalidate
              @submit.prevent="addEmployees"
            >
              <!-- Step 1: Basic -->
              <div v-show="currentStep === 1" class="space-y-5">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="join-label">Full name</label>
                    <div class="join-input-wrap">
                      <i class="join-input-icon" data-feather="user"></i>
                      <input
                        class="join-input"
                        v-model.trim="employeesForm.name"
                        type="text"
                        autocomplete="name"
                        placeholder="Your Name"
                      />
                    </div>
                  </div>

                  <div>
                    <label class="join-label">Job title</label>
                    <div class="join-input-wrap">
                      <i class="join-input-icon" data-feather="briefcase"></i>
                      <input
                        class="join-input"
                        v-model.trim="employeesForm.job_title"
                        type="text"
                        autocomplete="organization-title"
                        placeholder="Sales Manager"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label class="join-label">Branch</label>
                  <div class="join-input-wrap">
                    <i class="join-input-icon" data-feather="map-pin"></i>
                    <select
                      class="join-input join-select"
                      v-model="employeesForm.branch_id"
                      :disabled="!branches.length"
                    >
                      <option value="" disabled>
                        {{
                          branches.length
                            ? "Select a branch"
                            : "Loading branches..."
                        }}
                      </option>
                      <option
                        v-for="branch in branches"
                        :key="branch.id"
                        :value="branch.id"
                      >
                        {{ branch.name }}
                      </option>
                    </select>
                    <span class="join-select-caret" aria-hidden="true">
                      <i data-feather="chevron-down"></i>
                    </span>
                  </div>
                </div>

                <div
                  class="rounded-xl bg-gray-50 dark:bg-black/20 p-4 ring-1 ring-black/5 dark:ring-white/10"
                >
                  <div class="flex items-start gap-3">
                    <span class="join-hint-icon">
                      <i data-feather="shield"></i>
                    </span>
                    <p
                      class="text-sm text-ternary-dark dark:text-ternary-light/90"
                    >
                      Your details are reviewed by admin before going live.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Step 2: Contact -->
              <div v-show="currentStep === 2" class="space-y-5">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="join-label">Mobile</label>
                    <div class="join-input-wrap">
                      <i class="join-input-icon" data-feather="phone"></i>
                      <input
                        class="join-input"
                        v-model="employeesForm.mobile"
                        type="text"
                        inputmode="tel"
                        autocomplete="tel"
                        placeholder="+9715XXXXXXXX"
                        @input="restrictToNumbers"
                      />
                    </div>
                    <p
                      class="mt-2 text-xs text-ternary-dark dark:text-ternary-light/70"
                    >
                      Numbers and “+” only.
                    </p>
                  </div>

                  <div>
                    <label class="join-label">Email</label>
                    <div class="join-input-wrap">
                      <i class="join-input-icon" data-feather="mail"></i>
                      <input
                        class="join-input"
                        v-model.trim="employeesForm.email"
                        type="email"
                        autocomplete="email"
                        placeholder="name@gulftic.com"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label class="join-label">LinkedIn (optional)</label>
                  <div class="join-input-wrap">
                    <i class="join-input-icon" data-feather="linkedin"></i>
                    <input
                      class="join-input"
                      v-model.trim="employeesForm.linked_in"
                      type="text"
                      inputmode="url"
                      autocomplete="url"
                      placeholder="https://www.linkedin.com/in/your-profile"
                    />
                  </div>
                </div>
              </div>

              <!-- Step 3: Photo & Review -->
              <div v-show="currentStep === 3" class="space-y-6">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
                  <div
                    class="rounded-2xl bg-gray-50 dark:bg-black/20 p-5 ring-1 ring-black/5 dark:ring-white/10"
                  >
                    <p
                      class="text-sm font-general-semibold text-primary-dark dark:text-primary-light"
                    >
                      Profile photo
                    </p>
                    <p
                      class="mt-1 text-xs text-ternary-dark dark:text-ternary-light/70"
                    >
                      JPG/PNG recommended. Square works best.
                    </p>

                    <div class="mt-4 flex items-center gap-4">
                      <div class="join-avatar">
                        <img
                          :src="photoPreview || defaultAvatar"
                          alt="Profile preview"
                        />
                      </div>
                      <div class="flex flex-col gap-2">
                        <button
                          class="join-btn join-btn-secondary"
                          type="button"
                          @click="openFileInput"
                        >
                          <i data-feather="upload"></i>
                          Upload photo
                        </button>
                        <button
                          class="join-btn join-btn-ghost"
                          type="button"
                          :disabled="!photoPreview"
                          @click="clearPhoto"
                        >
                          <i data-feather="trash-2"></i>
                          Remove
                        </button>
                        <input
                          ref="fileInput"
                          type="file"
                          class="hidden"
                          accept="image/*"
                          @change="handleFileSelect"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    class="rounded-2xl bg-indigo-50/70 dark:bg-indigo-500/10 p-5 ring-1 ring-indigo-600/10 dark:ring-indigo-400/10 min-w-0"
                  >
                    <p
                      class="text-sm font-general-semibold text-primary-dark dark:text-primary-light"
                    >
                      Review
                    </p>
                    <dl class="mt-4 space-y-3 text-sm min-w-0">
                      <div class="join-review-row">
                        <dt class="join-review-label">Name</dt>
                        <dd class="join-review-value">
                          {{ employeesForm.name || "—" }}
                        </dd>
                      </div>
                      <div class="join-review-row">
                        <dt class="join-review-label">Job</dt>
                        <dd class="join-review-value">
                          {{ employeesForm.job_title || "—" }}
                        </dd>
                      </div>
                      <div class="join-review-row">
                        <dt class="join-review-label">Branch</dt>
                        <dd class="join-review-value">
                          {{ selectedBranchName || "—" }}
                        </dd>
                      </div>
                      <div class="join-review-row">
                        <dt class="join-review-label">Mobile</dt>
                        <dd class="join-review-value">
                          {{ employeesForm.mobile || "—" }}
                        </dd>
                      </div>
                      <div class="join-review-row">
                        <dt class="join-review-label">Email</dt>
                        <dd class="join-review-value">
                          {{ employeesForm.email || "—" }}
                        </dd>
                      </div>
                      <div class="join-review-row">
                        <dt class="join-review-label">LinkedIn</dt>
                        <dd
                          class="join-review-value join-review-value-url"
                          :title="employeesForm.linked_in || ''"
                        >
                          {{ employeesForm.linked_in || "Gulftic Company" }}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>

              <div v-if="error !== ''" class="mt-6">
                <div
                  class="rounded-xl px-4 py-3 ring-1"
                  :class="
                    responseState === 'successfully'
                      ? 'bg-green-50 text-green-800 ring-green-600/20 dark:bg-green-500/10 dark:text-green-200 dark:ring-green-400/20'
                      : 'bg-red-50 text-red-800 ring-red-600/20 dark:bg-red-500/10 dark:text-red-200 dark:ring-red-400/20'
                  "
                >
                  <div class="flex items-start gap-3">
                    <span class="mt-0.5">
                      <i
                        :data-feather="
                          responseState === 'successfully'
                            ? 'check-circle'
                            : 'alert-triangle'
                        "
                      ></i>
                    </span>
                    <p class="text-sm sm:text-base">{{ error }}</p>
                  </div>
                </div>
              </div>

              <div
                class="mt-8 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3"
              >
                <button
                  class="join-btn join-btn-ghost"
                  type="button"
                  :disabled="currentStep === 1 || loading"
                  @click="prevStep"
                >
                  <i data-feather="arrow-left"></i>
                  Back
                </button>

                <div
                  class="flex flex-col sm:flex-row gap-3 sm:justify-end sm:items-center"
                >
                  <button
                    v-if="currentStep < steps.length"
                    class="join-btn join-btn-primary"
                    type="button"
                    :disabled="loading"
                    @click="nextStep"
                  >
                    Next
                    <i data-feather="arrow-right"></i>
                  </button>

                  <button
                    v-else
                    class="join-btn join-btn-primary"
                    type="submit"
                    :disabled="loading"
                  >
                    <span
                      v-if="!loading"
                      class="inline-flex items-center gap-2"
                    >
                      <i data-feather="check"></i>
                      Save & Welcome
                    </span>
                    <span v-else class="inline-flex items-center gap-2">
                      <i data-feather="loader"></i>
                      Submitting...
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <loadingSpinner v-if="loading" class="join-loading" />
  </section>
</template>

<script>
import axios from "axios";
import feather from "feather-icons";
import loadingSpinner from "../components/reusable/LoadingSpannier.vue";

export default {
  components: {
    loadingSpinner,
  },

  data() {
    return {
      token: "",
      error: "",
      responseState: "",
      loading: false,
      branches: [],
      currentStep: 1,
      steps: [
        {
          key: "basic",
          title: "Your details",
          subtitle: "Name and role",
          icon: "user",
        },
        {
          key: "contact",
          title: "Contact",
          subtitle: "Phone, email, LinkedIn",
          icon: "phone",
        },
        {
          key: "photo",
          title: "Photo & review",
          subtitle: "Upload and confirm",
          icon: "image",
        },
      ],
      photoPreview: "",
      employeesForm: {
        name: "",
        job_title: "",
        branch_id: "",
        mobile: "+",
        email: "",
        linked_in: "",
        website_company: "",
        address: "",
        company_brief: "",
        whats_app: "",
        photo: "",
      },
    };
  },

  computed: {
    progressPercent() {
      if (!this.steps.length) return 0;
      return Math.round((this.currentStep / this.steps.length) * 100);
    },
    defaultAvatar() {
      const svg = encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 24 24" fill="none">
          <rect width="24" height="24" rx="12" fill="#EEF2FF"/>
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Z" fill="#3B82F6" opacity="0.35"/>
          <path d="M4 20c1.8-3.2 4.4-5 8-5s6.2 1.8 8 5" stroke="#3B82F6" stroke-width="1.5" stroke-linecap="round" opacity="0.55"/>
        </svg>`
      );
      return `data:image/svg+xml,${svg}`;
    },
    selectedBranchName() {
      const found = this.branches.find(
        (b) => String(b.id) === String(this.employeesForm.branch_id)
      );
      return found?.name || "";
    },
  },

  mounted() {
    this.getBranch();
    feather.replace();
  },

  updated() {
    feather.replace();
  },

  methods: {
    restrictToNumbers(event) {
      this.employeesForm.mobile = event.target.value.replace(/[^\d+]/g, "");
    },

    isValidLinkedInUrl(value) {
      if (!value || !value.trim()) return true;
      const withProtocol = /^https?:\/\//i.test(value)
        ? value.trim()
        : `https://${value.trim()}`;
      try {
        const url = new URL(withProtocol);
        return url.protocol === "http:" || url.protocol === "https:";
      } catch {
        return false;
      }
    },

    normalizeLinkedInUrl(value) {
      if (!value || !value.trim()) return "";
      const trimmed = value.trim();
      return /^https?:\/\//i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`;
    },

    getBranch() {
      axios
        .get("https://gulftic-system.fci.group/api/office")
        .then((res) => {
          this.branches = (res?.data?.data || []).map((item) => ({
            id: item.id,
            name: item.name,
          }));
        })
        .catch(() => {
          this.branches = [];
        });
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.employeesForm.photo = file;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.photoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    openFileInput() {
      this.$refs.fileInput.click();
    },

    clearPhoto() {
      this.employeesForm.photo = "";
      this.photoPreview = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    stepStateLabel(step) {
      if (step < this.currentStep) return "Done";
      if (step === this.currentStep) return "Current";
      return "Next";
    },

    stepBtnClass(step) {
      if (step === this.currentStep) {
        return "bg-indigo-50/70 dark:bg-indigo-500/10 ring-indigo-600/15 dark:ring-indigo-400/20";
      }
      if (step < this.currentStep) {
        return "bg-green-50/60 dark:bg-green-500/10 ring-green-600/15 dark:ring-green-400/20";
      }
      return "bg-white/40 dark:bg-white/5 ring-black/5 dark:ring-white/10 hover:bg-white/70 dark:hover:bg-white/10";
    },

    stepPillClass(step) {
      if (step < this.currentStep) return "join-pill-done";
      if (step === this.currentStep) return "join-pill-current";
      return "join-pill-next";
    },

    validateStep(step) {
      const missing = [];

      if (step === 1) {
        if (!this.employeesForm.name) missing.push("Name is required");
        if (!this.employeesForm.job_title)
          missing.push("Job title is required");
        if (!this.employeesForm.branch_id) missing.push("Branch is required");
      }

      if (step === 2) {
        if (!this.employeesForm.mobile || this.employeesForm.mobile === "+")
          missing.push("Mobile is required");
        if (!this.employeesForm.email) missing.push("Email is required");
        if (
          this.employeesForm.linked_in &&
          !this.isValidLinkedInUrl(this.employeesForm.linked_in)
        ) {
          missing.push(
            "Please enter a valid LinkedIn URL (e.g. https://www.linkedin.com/in/your-profile)"
          );
        }
      }

      if (missing.length) {
        this.responseState = "error";
        this.error = missing[0];
        return false;
      }

      return true;
    },

    nextStep() {
      this.error = "";
      this.responseState = "";
      if (!this.validateStep(this.currentStep)) return;
      this.currentStep = Math.min(this.currentStep + 1, this.steps.length);
    },

    prevStep() {
      this.error = "";
      this.responseState = "";
      this.currentStep = Math.max(this.currentStep - 1, 1);
    },

    goToStep(step) {
      if (step <= this.currentStep) {
        this.currentStep = step;
        return;
      }
      if (!this.validateStep(this.currentStep)) return;
      this.currentStep = step;
    },

    addEmployees() {
      if (!this.validateStep(1)) {
        this.currentStep = 1;
        return;
      }
      if (!this.validateStep(2)) {
        this.currentStep = 2;
        return;
      }

      this.loading = true;

      if (this.employeesForm.linked_in === "") {
        this.employeesForm.linked_in =
          "https://www.linkedin.com/company/gulfticgroup";
      } else {
        this.employeesForm.linked_in = this.normalizeLinkedInUrl(
          this.employeesForm.linked_in
        );
      }

      const formData = new FormData();
      formData.append("linked_in", this.employeesForm.linked_in);
      formData.append("name", this.employeesForm.name);
      formData.append("job_title", this.employeesForm.job_title);
      formData.append("branch_id", this.employeesForm.branch_id);
      formData.append("email", this.employeesForm.email);
      formData.append("mobile", this.employeesForm.mobile);
      formData.append("website_company", "https://gulftic.com");
      formData.append("address", "adress");
      formData.append("company_brief", "brief");
      formData.append("whats_app", this.employeesForm.mobile);
      formData.append("photo", this.employeesForm.photo);

      axios
        .post(`https://gulftic-system.fci.group/api/employee`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json, text/plain, */*",
          },
        })
        .then((res) => {
          if (res.status === 200) {
            this.loading = false;
            this.responseState = "successfully";
            this.error =
              "Welcome to Gulftic. Please wait for the admin to verify your information. Your details will be sent to your Email.";

            this.employeesForm = {
              name: "",
              job_title: "",
              branch_id: "",
              mobile: "+",
              email: "",
              linked_in: "",
              website_company: "",
              address: "",
              company_brief: "",
              whats_app: "",
              photo: "",
            };
            this.photoPreview = "";
            this.currentStep = 1;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.responseState = "error";
          this.error =
            error?.response?.data?.message ||
            "Something went wrong. Please try again.";
          console.error("Error adding employee:", error);
        });
    },
  },
};
</script>

<style scoped>
.join-page {
  position: relative;
  min-height: 100vh;
}

.join-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      1200px 600px at 10% 0%,
      rgba(59, 130, 246, 0.18),
      transparent 60%
    ),
    radial-gradient(
      900px 500px at 95% 20%,
      rgba(99, 102, 241, 0.18),
      transparent 55%
    ),
    radial-gradient(
      900px 500px at 50% 110%,
      rgba(14, 165, 233, 0.16),
      transparent 55%
    ),
    linear-gradient(180deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.85));
}

.dark .join-bg {
  background: radial-gradient(
      1200px 600px at 10% 0%,
      rgba(59, 130, 246, 0.22),
      transparent 60%
    ),
    radial-gradient(
      900px 500px at 95% 20%,
      rgba(99, 102, 241, 0.22),
      transparent 55%
    ),
    radial-gradient(
      900px 500px at 50% 110%,
      rgba(14, 165, 233, 0.18),
      transparent 55%
    ),
    linear-gradient(180deg, rgba(3, 7, 18, 0.65), rgba(3, 7, 18, 0.85));
}

.join-badge-dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #2563eb, #6366f1);
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
}

.join-progress {
  background: linear-gradient(90deg, #2563eb, #6366f1);
}

.join-step-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.1);
}

.dark .join-step-icon {
  background: rgba(99, 102, 241, 0.14);
}

.join-step-icon :deep(svg) {
  width: 18px;
  height: 18px;
  color: #4f46e5;
}

.dark .join-step-icon :deep(svg) {
  color: #a5b4fc;
}

.join-step-pill {
  padding: 4px 8px;
  border-radius: 9999px;
}

.join-pill-current {
  background: rgba(79, 70, 229, 0.12);
  color: #3730a3;
}

.dark .join-pill-current {
  background: rgba(165, 180, 252, 0.14);
  color: #c7d2fe;
}

.join-pill-done {
  background: rgba(16, 185, 129, 0.12);
  color: #065f46;
}

.dark .join-pill-done {
  background: rgba(16, 185, 129, 0.12);
  color: #a7f3d0;
}

.join-pill-next {
  background: rgba(107, 114, 128, 0.1);
  color: #374151;
}

.dark .join-pill-next {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
}

.join-label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.9rem;
  color: rgba(55, 65, 81, 0.95);
}

.dark .join-label {
  color: rgba(255, 255, 255, 0.82);
}

.join-input-wrap {
  position: relative;
}

.join-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.7;
}

.join-input-icon :deep(svg) {
  width: 18px;
  height: 18px;
}

.join-input {
  width: 100%;
  border-radius: 14px;
  padding: 12px 14px 12px 42px;
  background: rgba(249, 250, 251, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  outline: none;
  transition: box-shadow 200ms ease, border-color 200ms ease,
    background 200ms ease;
  color: rgba(17, 24, 39, 0.95);
}

.join-select {
  appearance: none;
  padding-right: 42px;
  cursor: pointer;
}

.join-select:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.join-select-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.75;
}

.join-select-caret :deep(svg) {
  width: 18px;
  height: 18px;
}

.dark .join-input {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
}

.join-input:focus {
  border-color: rgba(79, 70, 229, 0.35);
  box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.15);
}

.join-hint-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
}

.join-hint-icon :deep(svg) {
  width: 18px;
  height: 18px;
  color: #2563eb;
}

.join-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 14px;
  padding: 12px 16px;
  font-weight: 600;
  white-space: nowrap;
  transition: transform 120ms ease, background 200ms ease, box-shadow 200ms ease,
    opacity 200ms ease;
  user-select: none;
}

.join-btn:active {
  transform: translateY(1px);
}

.join-btn :deep(svg) {
  width: 18px;
  height: 18px;
}

.join-btn-primary {
  background: linear-gradient(135deg, #2563eb, #6366f1);
  color: white;
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
}

.join-btn-primary:hover {
  box-shadow: 0 18px 40px rgba(37, 99, 235, 0.24);
}

.join-btn-secondary {
  background: rgba(255, 255, 255, 0.85);
  color: rgba(17, 24, 39, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.dark .join-btn-secondary {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.join-btn-ghost {
  background: transparent;
  color: rgba(55, 65, 81, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.dark .join-btn-ghost {
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.join-btn[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
}

.join-avatar {
  width: 104px;
  height: 104px;
  border-radius: 9999px;
  overflow: hidden;
  border: 2px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 14px 25px rgba(99, 102, 241, 0.15);
  background: rgba(255, 255, 255, 0.6);
}

.dark .join-avatar {
  background: rgba(255, 255, 255, 0.06);
}

.join-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.join-loading {
  position: fixed;
  inset: 0;
  z-index: 50;
}

.join-review-row {
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 12px;
  align-items: start;
  min-width: 0;
}
.join-review-label {
  color: rgba(55, 65, 81, 0.85);
}
.dark .join-review-label {
  color: rgba(255, 255, 255, 0.72);
}
.join-review-value {
  min-width: 0;
  text-align: right;
  font-weight: 700;
  color: rgba(17, 24, 39, 0.95);
  overflow-wrap: anywhere;
  word-break: break-word;
}
.dark .join-review-value {
  color: rgba(255, 255, 255, 0.92);
}
.join-review-value-url {
  line-height: 1.25rem;
}

@media (max-width: 480px) {
  .join-review-row {
    grid-template-columns: 1fr;
  }
  .join-review-value {
    text-align: left;
  }
}
</style>
